// apps/frontend/src/lib/userActivityTracker.ts
// Tracker d'activité utilisateur réelle (pas juste "tab ouvert").
//
// Pourquoi : last_activity_at en BDD était bumpé à chaque requête authentifiée
// (incluant les polls passifs), donc un onglet ouvert sans interaction
// apparaissait comme "actif maintenant" dans l'admin live-users. On distingue
// désormais "tab ouvert" (page visible) vs "user interagit vraiment"
// (mousemove/click/scroll/keypress dans la dernière fenêtre).
//
// L'apiClient lit `isUserActive()` pour décider d'envoyer le header
// X-User-Active sur ses requêtes. Le middleware backend ne bump
// last_activity_at que si ce header est présent (ou si la méthode est une
// mutation, qui est par construction une interaction).

const ACTIVITY_WINDOW_MS = 60_000; // 60s : on considère "actif" si interaction récente

let lastInteractionMs: number = 0;
let initialized = false;

function recordInteraction() {
  lastInteractionMs = Date.now();
}

export function isUserActive(): boolean {
  if (typeof document !== 'undefined' && document.hidden) return false;
  return Date.now() - lastInteractionMs < ACTIVITY_WINDOW_MS;
}

export function initUserActivityTracker() {
  if (typeof window === 'undefined' || initialized) return;
  initialized = true;

  // Liste minimale d'événements qui prouvent une interaction réelle.
  // mousemove est volontairement throttlé via passive listeners pour ne pas
  // peser sur la perf (chaque event = juste une assignation).
  const events = [
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'wheel',
    'click',
  ] as const;

  for (const evt of events) {
    window.addEventListener(evt, recordInteraction, { passive: true });
  }

  // mousemove peut spammer — on throttle à 1 enregistrement par seconde max.
  let lastMouseMoveMs = 0;
  window.addEventListener(
    'mousemove',
    () => {
      const now = Date.now();
      if (now - lastMouseMoveMs > 1_000) {
        lastMouseMoveMs = now;
        recordInteraction();
      }
    },
    { passive: true }
  );

  // Quand l'onglet devient visible, on considère que l'user "regarde" mais on
  // n'enregistre pas une interaction de plein droit — il faut une vraie action.
  // En revanche on remet à zéro pour ne pas hériter d'une vieille activité.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      lastInteractionMs = 0;
    }
  });
}
